import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomDatePicker from "../../common/CustomDatePicker";
import CustomTextField from "../../common/CustomTextField";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  Cancel,
  CheckCircleOutline,
  PaymentsOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import {
  SingleAlertInfo,
  setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { RESPONSE_CODES } from "../../../constants/applicationConstants";
import {
  AddPaymentInfo,
  saveRecordPaymentInfo,
} from "../../../redux/slice/payment/paymentSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { priceFormatter } from "../../../utils/Utility";
import { getInvoiceById, InvoiceDetailDTO } from "../../../redux/slice/invoice/InvoiceSlice";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

export type AddPaymentDetailProps = {
  invoiceIdOverride?: number;
  onRequestClose?: () => void;
  onSaved?: () => void;
  compact?: boolean;
  initialPaymentDate?: string;
  initialPaidAmount?: string | number;
  /** Form field name ("paymentDate" or "paidAmount") to visually highlight and scroll to —
   *  used when this popup was opened via a specific cell's pencil icon in the sales sheet. */
  highlightFieldName?: string;
};

const AddPaymentDetail = ({
  invoiceIdOverride,
  onRequestClose,
  onSaved,
  compact,
  initialPaymentDate,
  initialPaidAmount,
  highlightFieldName,
}: AddPaymentDetailProps = {}) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      paymentDate: initialPaymentDate || undefined,
      paidAmount: initialPaidAmount !== undefined && initialPaidAmount !== "" ? Number(initialPaidAmount) : undefined,
    },
  });

  const { navigate } = useNavigation();
  const { state: locationState } = useLocation();
  const state: any = locationState ?? {};
  const invoiceId = invoiceIdOverride ?? state.invoiceId;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (initialPaymentDate) setValue("paymentDate", initialPaymentDate);
    if (initialPaidAmount !== undefined && initialPaidAmount !== "") {
      setValue("paidAmount", Number(initialPaidAmount));
    }
  }, [initialPaymentDate, initialPaidAmount, setValue]);
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    datepickerWrapSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetailDTO>();
  const [paidAmount, setPaidAmount ] = useState(0);
  const [remainingAmount, setRemainingAmount ] = useState(0);

  const onSubmit = async (data: AddPaymentInfo) => {
    const paymentData = {
      invoiceId,
      paymentDate: dayjs(data?.paymentDate).format("MM/DD/YYYY"),
      paidAmount: data?.paidAmount,
      paymentComments: data?.paymentComments,
    };

    const response = await dispatch(saveRecordPaymentInfo(paymentData));
    if (response?.payload?.code === RESPONSE_CODES.SUCCESS_CODE) {
      let alertMessage = "Payment recorded successfully!";
      const alertObj: SingleAlertInfo = {
        message: alertMessage,
        alertType: "success",
      };
      dispatch(setSingleAlertObj(alertObj));
      onSaved?.();
      if (!onRequestClose) {
        //CS-1644 Navigate to payment list after doing record payment
        navigate("/payment_list", { state: { paymentListSearchCriteria: { invoiceNumber: state.invNumber } } });
      } else {
        onRequestClose();
      }
    } else if (response?.payload?.code === RESPONSE_CODES.FAILED_CODE) {
      let alertMessage = response.payload?.message;
      const alertObj: SingleAlertInfo = {
        message: alertMessage,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
  };

  useEffect(() => {
    if (invoiceId) setInvoiceDetails(invoiceId);
  }, [invoiceId]);

  useEffect(() => {
    if (selectedInvoice?.payment?.totalAmount) {
      setPaidAmount(parseFloat(selectedInvoice.payment.totalAmount));
    };  
    setRemainingAmount(selectedInvoice?.payment?.remainingAmount ??
        selectedInvoice?.netCogsAmount ??
        0);
  }, [selectedInvoice]);


  const setInvoiceDetails = async (invoiceId: number) => {
      const response = await dispatch(getInvoiceById({invoiceId: invoiceId, isSaveAuditLog: true}));
      if(response?.payload?.code === SUCCESS_CODE && response?.payload?.data) {
        setSelectedInvoice(response.payload.data);
      }
  };

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.9, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  const highlightFieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!highlightFieldName) return;
    const el = highlightFieldRefs.current[highlightFieldName];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightFieldName]);

  const highlightFieldSx = (fieldName: string): Record<string, unknown> =>
    highlightFieldName === fieldName
      ? {
          "@keyframes highlightFieldRadar": {
            "0%": { boxShadow: `0 0 0 0 ${mTokens.accent}99` },
            "100%": { boxShadow: `0 0 0 12px ${mTokens.accent}00` },
          },
          "& .MuiOutlinedInput-root": {
            animation: "highlightFieldRadar 1.6s ease-out infinite",
          },
          "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
            borderColor: `${mTokens.accent} !important`,
            borderWidth: "2px !important",
          },
        }
      : {};

  const InfoRow = ({ label, value, highlight = false }: { label: string; value?: React.ReactNode; highlight?: boolean }) => (
    <Box display="flex" alignItems="center" py={1.15} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0, fontWeight: highlight ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: highlight ? "1rem" : "0.88rem",
          color: highlight ? "#10B981" : mTokens.textPrimary,
          fontWeight: highlight ? 800 : 600,
          flex: 1,
        }}
        component="div"
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );

  const invoiceAmountDisplay =
    typeof state.invoiceAmount === "string"
      ? state.invoiceAmount
      : priceFormatter(state.invoiceAmount);

  if (compact) {
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={cardSx}>
          <SectionHeader icon={<PaymentsOutlined fontSize="small" />} title="Payment Details" />
          <Box sx={{ px: 3.2, pt: 2.2, pb: 3.2 }}>
            <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, mb: 2.5 }}>
              Please add payment details.
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: mTokens.textSecondary }}>
                  Invoice #
                </Typography>
                <Typography sx={{ fontWeight: 700, color: mTokens.textPrimary }}>
                  {selectedInvoice?.mfgInvoiceNumber || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: mTokens.textSecondary }}>
                  Order #
                </Typography>
                <Typography sx={{ fontWeight: 700, color: mTokens.textPrimary }}>
                  {selectedInvoice?.orderNumber || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Payment Date" required />
                <Box
                  ref={(el: HTMLDivElement | null) => (highlightFieldRefs.current.paymentDate = el)}
                  sx={[datepickerWrapSx(!!errors.paymentDate), highlightFieldSx("paymentDate")]}
                >
                  <CustomDatePicker
                    controllerName="paymentDate"
                    control={control}
                    label=""
                    size="small"
                    rules={{ required: "Payment date is required" }}
                    popperZIndex={1700}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <FieldLabel label="Amount" required />
                <Box
                  ref={(el: HTMLDivElement | null) => (highlightFieldRefs.current.paidAmount = el)}
                  sx={[inputFieldSx(!!errors.paidAmount), highlightFieldSx("paidAmount")]}
                >
                  <CustomCurrencyField
                    controllerName="paidAmount"
                    control={control}
                    label=""
                    size="small"
                    rules={{
                      required: "Amount is required",
                      min: {
                        value: 0.01,
                        message: "Minimum value allowed is 0.01",
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel label="Comments" />
                <Box sx={inputFieldSx()}>
                  <CustomTextField
                    control={control}
                    controllerName="paymentComments"
                    label=""
                    size="small"
                    multiline
                    rows={4}
                    placeholder="Enter comments..."
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.2} justifyContent="flex-end" mt={1}>
                  <Button
                    size="large"
                    startIcon={<CheckCircleOutline />}
                    type="submit"
                    removeUpperCase
                    sx={{
                      ...primaryBtnSx,
                      minWidth: { sm: 180 },
                      backgroundColor: mTokens.accent,
                      boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                      "& .MuiTypography-root": { color: "#ffffff !important" },
                      "& .MuiButton-startIcon": { color: "#ffffff" },
                    }}
                  >
                    Record Payment
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    removeUpperCase
                    startIcon={<Cancel />}
                    onClick={() => (onRequestClose ? onRequestClose() : navigate(-1))}
                    sx={{
                      ...cancelBtnSx,
                      minWidth: { sm: 140 },
                      "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                      "& .MuiButton-startIcon": { color: mTokens.error },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </form>
    );
  }

  return (
    <Box
      sx={{
        mx: { xs: "10px", sm: "20px" },
        mt: "12px",
        pb: "72px",
      }}
    >
      <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} />
        <Box data-testid="mock-header">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Billing / Record Payment
          </Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            Record Payment
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            Record a payment for the selected invoice.
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2, alignItems: "flex-start" }}>
          <Box sx={{ ...cardSx, order: { xs: 2, md: 1 } }}>
            <SectionHeader icon={<PaymentsOutlined fontSize="small" />} title="Payment Details" />
            <Box sx={{ px: 3.2, pt: 2.2, pb: 3.2 }}>
              <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, mb: 2.5 }}>
                Please add payment details.
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <FieldLabel label="Payment Date" required />
                  <Box sx={datepickerWrapSx(!!errors.paymentDate)}>
                    <CustomDatePicker
                      controllerName="paymentDate"
                      control={control}
                      label=""
                      size="small"
                      rules={{ required: "Payment date is required" }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel label="Amount" required />
                  <Box sx={inputFieldSx(!!errors.paidAmount)}>
                    <CustomCurrencyField
                      controllerName="paidAmount"
                      control={control}
                      label=""
                      size="small"
                      rules={{
                        required: "Amount is required",
                        min: {
                          value: 0.01,
                          message: "Minimum value allowed is 0.01",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel label="Comments" />
                  <Box sx={inputFieldSx()}>
                    <CustomTextField
                      control={control}
                      controllerName="paymentComments"
                      label=""
                      size="small"
                      multiline
                      rows={4}
                      placeholder="Enter comments..."
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.2} justifyContent="flex-end" mt={1}>
                    <Button
                      size="large"
                      startIcon={<CheckCircleOutline />}
                      type="submit"
                      removeUpperCase
                      sx={{
                        ...primaryBtnSx,
                        minWidth: { sm: 180 },
                        backgroundColor: mTokens.accent,
                        boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                        "& .MuiTypography-root": { color: "#ffffff !important" },
                        "& .MuiButton-startIcon": { color: "#ffffff" },
                      }}
                    >
                      Record Payment
                    </Button>
                    <Button
                      color="error"
                      variant="outlined"
                      removeUpperCase
                      startIcon={<Cancel />}
                      onClick={() => navigate(-1)}
                      sx={{
                        ...cancelBtnSx,
                        minWidth: { sm: 140 },
                        "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                        "& .MuiButton-startIcon": { color: mTokens.error },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ ...cardSx, order: { xs: 1, md: 2 } }}>
            <SectionHeader icon={<ReceiptLongOutlined fontSize="small" />} title="Invoice Info" />
            <Box sx={{ px: 3.2, py: 2 }}>
              <InfoRow label="INV #" value={state.invNumber} />
              {selectedInvoice?.purchaseAndBill === "Y" && (
                <InfoRow label="Invoice Reference Id" value={selectedInvoice?.invReferenceId || "-"} />
              )}
              <InfoRow label="INV Date" value={state.invDate} />
              <InfoRow label="Practice" value={state.practice} />
              <InfoRow label="MFR" value={state.mfr} />
              <Box sx={{ borderTop: `1px solid ${mTokens.border}`, mt: 1, pt: 1 }}>
                <InfoRow label="Invoice Amount" value={invoiceAmountDisplay} />
                <InfoRow label="Paid Amount" value={paidAmount && priceFormatter(paidAmount) || priceFormatter(0)} />
                <InfoRow label="Credit Amount" value={priceFormatter(selectedInvoice?.creditAmount || 0)} />
                <InfoRow
                  label="Remaining Amount"
                  value={remainingAmount && priceFormatter(remainingAmount) || priceFormatter(0)}
                  highlight
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default AddPaymentDetail;
